import type {
	AnalysisCheck,
	AnalysisEnvironment,
	AnalysisInput,
	AnalysisPlan,
	AnalysisRun,
	AnalysisView,
	EnvironmentManifest,
	ExternalExecution,
	ManagedExecution,
	ManifestComparison,
	PlanSensitivity,
	ResultComparison,
	RunEnvironment,
} from "../../../shared/wire/research.ts";
import {
	analysisTypeLabel,
	blockReasonLabel,
	checkStatusLabel,
	checkTypeLabel,
	comparisonLabel,
	environmentOriginLabel,
	executionLabel,
	integrityLabel,
	keyComparisonLabel,
	manifestLabel,
	methodValidityLabel,
	receiptLabel,
	reconstructionLabel,
	runOriginLabel,
	sourceStatusLabel,
	timeLabel,
} from "./researchLabels";

// The analysis domain as the server returned it: every hash in full, every state on its own axis, and nothing
// a run did not observe drawn as if it had.

const join = (parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" · ");

const Hash = ({ value }: { value: string | null | undefined }) => <code className="research-hash">{value ?? "无"}</code>;

const json = (value: unknown) => JSON.stringify(value);

function manifestText(m: EnvironmentManifest | null | undefined): string {
	if (!m) return "";
	const packages = m.packages ? Object.entries(m.packages) : [];
	return join([
		m.python && `Python ${m.python}`,
		m.implementation,
		m.platform,
		m.architecture,
		m.os,
		m.image && `镜像 ${m.image}`,
		m.packages === null ? "依赖未知" : packages.length ? `依赖 ${packages.map(([n, v]) => `${n} ${v}`).join("、")}` : "无第三方依赖",
	]);
}

/** One view's analysis objects: an environment check, then runs, plans, inputs and environments. */
export function AnalysisSection({ analysis, empty }: { analysis: AnalysisView; empty?: boolean }) {
	const nothing = !analysis.comparison && !analysis.runs.length && !analysis.plans.length && !analysis.inputs.length && !analysis.environments.length;
	if (nothing) return empty ? <p className="hint">暂无分析</p> : null;
	return (
		<>
			{analysis.comparison && <ManifestCheck comparison={analysis.comparison} />}
			{analysis.runs.length > 0 && (
				<div className="research-block">
					<p className="research-subtitle">运行</p>
					{analysis.runs.map((r) => (
						<RunCard key={r.id} run={r} />
					))}
				</div>
			)}
			{analysis.plans.length > 0 && (
				<div className="research-block">
					<p className="research-subtitle">计划</p>
					{analysis.plans.map((p) => (
						<PlanCard key={`${p.id}:${p.revision}`} plan={p} />
					))}
				</div>
			)}
			{analysis.inputs.length > 0 && <InputList inputs={analysis.inputs} />}
			{analysis.environments.length > 0 && <EnvironmentList environments={analysis.environments} />}
		</>
	);
}

function ManifestCheck({ comparison }: { comparison: ManifestComparison }) {
	return (
		<div className="research-block research-claim">
			<p className="research-meta">
				<span className={`research-state ${comparison.status}`}>环境核对 {manifestLabel(comparison.status)}</span> · 仅比对清单 · 重建 {reconstructionLabel(comparison.reconstruction)}
			</p>
			<Reasons reasons={comparison.reasons} />
		</div>
	);
}

function Reasons({ reasons }: { reasons: string[] }) {
	if (!reasons.length) return null;
	return (
		<ul className="research-list">
			{reasons.map((r, i) => (
				<li key={i} className="hint">
					{r}
				</li>
			))}
		</ul>
	);
}

function RunCard({ run }: { run: AnalysisRun }) {
	const execution = run.execution;
	const reportedCode = typeof run.reportedCodeHash === "string" ? run.reportedCodeHash : null;
	return (
		<div className="research-block research-claim">
			<p className="research-claim-text">
				{run.title ?? `运行 ${run.id}`} <span className={`research-state ${run.execution.status}`}>{executionLabel(run.execution.status)}</span>
			</p>
			<p className="research-meta">
				{join([
					`运行 ${run.id}`,
					run.replayOf && `重放自 ${run.replayOf}`,
					runOriginLabel(run.origin),
					analysisTypeLabel(run.analysisType),
					run.planId ? `计划 ${run.planId} r${run.planRevision}` : "无冻结计划",
					`种子 ${run.seed ?? "无"}`,
					timeLabel(run.recordedAt),
				])}
			</p>
			<p className="research-meta">
				<span className="research-state not_assessed">方法有效性 {methodValidityLabel(run.methodValidity)}</span>
			</p>
			{Object.keys(run.parameters).length > 0 && <p className="research-meta">参数 {json(run.parameters)}</p>}
			{run.cleaningRules.length > 0 && <p className="research-meta">清洗规则：{run.cleaningRules.join("；")}</p>}
			{run.exclusionRules.length > 0 && <p className="research-meta">排除规则：{run.exclusionRules.join("；")}</p>}
			<p className="research-meta">
				代码 SHA-256 <Hash value={run.codeHash} />
				{run.code && ` · ${run.code.path}`}
				{run.command.length > 0 && ` · 命令 ${run.command.join(" ")}`}
			</p>
			{reportedCode && (
				<p className="research-meta">
					报告的代码 SHA-256 <Hash value={reportedCode} />
				</p>
			)}
			{run.inputHashes.map((i) => (
				<p key={`${i.inputId}:${i.mount}`} className="research-meta">
					输入 {i.mount} · {i.path} · 输入 {i.inputId} · SHA-256 <Hash value={i.sha256} />
				</p>
			))}
			{run.origin === "external" && run.inputHashes.length === 0 && <p className="research-meta">输入未登记</p>}
			<RunEnvironmentRows environment={run.environment} />
			{execution.origin === "managed" ? <ExecutionRows execution={execution} /> : <ExternalExecutionRows execution={execution} />}
			<ComparisonRows comparison={run.comparison} />
			{run.results && <p className="research-meta">结果 {json(run.results)}</p>}
			{run.reportedResults !== undefined && <p className="research-meta">报告的结果 {json(run.reportedResults)}</p>}
			{run.outputs.map((o) => (
				<p key={o.path} className="research-meta">
					输出 {o.path} · {o.size} B · SHA-256 <Hash value={o.sha256} />
				</p>
			))}
			<SensitivityRows items={run.sensitivity} />
			<CheckRows checks={run.checks} />
		</div>
	);
}

function RunEnvironmentRows({ environment }: { environment: RunEnvironment }) {
	return (
		<>
			<p className="research-meta">
				环境 {environmentOriginLabel(environment.origin)} · 摘要 <Hash value={environment.digest} /> · 重建 {reconstructionLabel(environment.reconstruction)} · 比对{" "}
				<span className={`research-state ${environment.comparison.status}`}>{manifestLabel(environment.comparison.status)}</span>
			</p>
			{environment.observed && <p className="research-meta">观测环境 {manifestText(environment.observed)}</p>}
			{(environment.recorded ?? []).map((r, i) => (
				<p key={i} className="research-meta">
					{r.origin === "reported" ? "报告环境" : "观测环境"} {r.environmentId ?? r.runId ?? ""} · 摘要 <Hash value={r.digest} /> · {manifestText(r.manifest)}
				</p>
			))}
			<Reasons reasons={environment.comparison.reasons} />
		</>
	);
}

function ExecutionRows({ execution }: { execution: ManagedExecution }) {
	const blocked = execution.reason ? blockReasonLabel(execution.reason) : null;
	return (
		<>
			<p className="research-meta">
				{join([
					`回执 ${receiptLabel(execution.receiptStatus)}`,
					execution.exitCode !== null && `退出码 ${execution.exitCode}`,
					execution.signal && `信号 ${execution.signal}`,
					execution.timedOut && "已超时",
					execution.startedAt && `开始 ${timeLabel(Date.parse(execution.startedAt))}`,
					execution.finishedAt && `结束 ${timeLabel(Date.parse(execution.finishedAt))}`,
				])}
			</p>
			<p className="research-meta">
				{join([
					`限额 墙钟 ${execution.limits.wallMs} ms`,
					`CPU ${execution.limits.cpuSeconds} s`,
					`内存 ${execution.limits.memoryMb} MB`,
					execution.isolation.mode === "unavailable" ? "隔离 已停用" : `隔离 ${execution.isolation.backend ?? "未确认"}`,
				])}
			</p>
			{blocked && (
				<p className="research-notice execution_blocked">
					{blocked.label}
					{blocked.detail ? `：${blocked.detail}` : ""}
				</p>
			)}
			{(execution.stdout || execution.stderr) && (
				<details className="research-raw">
					<summary>标准输出与错误输出</summary>
					{execution.stdout && <pre>{execution.stdout}</pre>}
					{execution.stdoutTruncated && <p className="hint">标准输出已截断</p>}
					{execution.stderr && <pre className="research-stderr">{execution.stderr}</pre>}
					{execution.stderrTruncated && <p className="hint">错误输出已截断</p>}
				</details>
			)}
		</>
	);
}

function ExternalExecutionRows({ execution }: { execution: ExternalExecution }) {
	const reported = typeof execution.reportedStatus === "string" ? execution.reportedStatus : null;
	return (
		<p className="research-meta">
			{join(["本机未运行", `回执 ${receiptLabel(execution.receiptStatus)}`, reported && `报告的状态 ${reported}`])}
		</p>
	);
}

function ComparisonRows({ comparison }: { comparison: ResultComparison }) {
	return (
		<>
			<p className="research-meta">
				结果比对 <span className={`research-state ${comparison.status}`}>{comparisonLabel(comparison.status)}</span>
				{comparison.keys.length > 0 && <span className="hint"> {comparison.rule}</span>}
			</p>
			{comparison.reason && <p className="hint">{comparison.reason}</p>}
			{comparison.keys.length > 0 && (
				<ul className="research-list">
					{comparison.keys.map((k) => (
						<li key={k.key}>
							{k.key} <span className={`research-state ${k.status}`}>{keyComparisonLabel(k.status)}</span>
							<span className="hint">
								{" "}
								{join([
									`期望 ${k.expected}`,
									k.actual !== undefined && `实际 ${json(k.actual)}`,
									`容差 ${k.tolerance}（绝对 ${k.absTolerance}，相对 ${k.relTolerance}）`,
									k.difference !== undefined && `差值 ${k.difference}`,
									k.reason,
								])}
							</span>
						</li>
					))}
				</ul>
			)}
		</>
	);
}

function SensitivityRows({ items }: { items: PlanSensitivity[] }) {
	if (!items.length) return null;
	return (
		<ul className="research-list">
			{items.map((s, i) => (
				<li key={i}>
					敏感性分析 {s.name} <span className={`research-state ${s.status}`}>{checkStatusLabel(s.status)}</span>
					{s.reason && <span className="hint"> {s.reason}</span>}
				</li>
			))}
		</ul>
	);
}

function CheckRows({ checks }: { checks: AnalysisCheck[] }) {
	if (!checks.length) return null;
	return (
		<ul className="research-list">
			{checks.map((c, i) => (
				<li key={i}>
					{checkTypeLabel(c.type)}
					{c.name ? ` ${c.name}` : ""} <span className={`research-state ${c.status}`}>{c.type === "method_validity" ? methodValidityLabel("not_assessed") : checkStatusLabel(c.status)}</span>
					{c.reportedStatus && <span className="hint"> 报告的状态 {c.reportedStatus}</span>}
					{c.reason && <span className="hint"> {c.reason}</span>}
				</li>
			))}
		</ul>
	);
}

function PlanCard({ plan }: { plan: AnalysisPlan & { revisions?: number } }) {
	return (
		<div className="research-block research-claim">
			<p className="research-claim-text">{plan.title}</p>
			<p className="research-meta">
				{join([
					`计划 ${plan.id} r${plan.revision}`,
					plan.revisions !== undefined && plan.revisions > plan.revision && `最新 r${plan.revisions}`,
					analysisTypeLabel(plan.analysisType),
					`种子 ${plan.seed ?? "无"}`,
					`冻结于 ${timeLabel(plan.frozenAt)}`,
				])}
			</p>
			{plan.amendment && (
				<p className="research-meta">
					修订自 r{plan.amendment.fromRevision}：{plan.amendment.reason}
				</p>
			)}
			{plan.analyses.length > 0 && <p className="research-meta">分析：{plan.analyses.join("；")}</p>}
			{Object.keys(plan.parameters).length > 0 && <p className="research-meta">参数 {json(plan.parameters)}</p>}
			{plan.cleaningRules.length > 0 && <p className="research-meta">清洗规则：{plan.cleaningRules.join("；")}</p>}
			{plan.exclusionRules.length > 0 && <p className="research-meta">排除规则：{plan.exclusionRules.join("；")}</p>}
			<SensitivityRows items={plan.sensitivity} />
			<CheckRows checks={plan.checks} />
		</div>
	);
}

function InputList({ inputs }: { inputs: AnalysisInput[] }) {
	return (
		<div className="research-block">
			<p className="research-subtitle">原始输入</p>
			<ul className="research-list">
				{inputs.map((i) => (
					<li key={i.id}>
						{i.path}{" "}
						<span className="hint">
							输入 {i.id} · {i.size} B · 快照 {timeLabel(i.capturedAt)}
						</span>
						<p className="research-meta">
							SHA-256 <Hash value={i.sha256} />
						</p>
						<p className="research-meta">
							快照 <span className={`research-state ${i.integrity}`}>{integrityLabel(i.integrity)}</span> · 原始文件{" "}
							<span className={`research-state ${i.originalSourceStatus}`}>{sourceStatusLabel(i.originalSourceStatus)}</span> · 核对 {timeLabel(i.lastCheckedAt)}
						</p>
						{i.originalSha256 && (
							<p className="research-meta">
								原始文件现 SHA-256 <Hash value={i.originalSha256} />
							</p>
						)}
						{i.reason && <p className="hint">{i.reason}</p>}
					</li>
				))}
			</ul>
		</div>
	);
}

function EnvironmentList({ environments }: { environments: AnalysisEnvironment[] }) {
	return (
		<div className="research-block">
			<p className="research-subtitle">登记环境</p>
			<ul className="research-list">
				{environments.map((e) => (
					<li key={e.id}>
						环境 {e.id} · {environmentOriginLabel(e.origin)} · 重建 {reconstructionLabel(e.reconstruction)}
						{e.sourceRef && ` · ${e.sourceRef}`}
						<p className="research-meta">
							摘要 <Hash value={e.digest} />
						</p>
						<p className="research-meta">{manifestText(e.manifest)}</p>
					</li>
				))}
			</ul>
		</div>
	);
}
